/**
 * Pixel's two quest celebrations, played on the spot (in the spirit of the full-charge short):
 *  found  all five shards: crouch, spin-launch straight up, a big backflip at the top, drop and
 *         stick the landing, then a little bow
 *  won    timing game beaten: swoop out and barrel-roll back across, a hopping pirouette, then a
 *         happy shimmy
 * Offsets are added on top of wherever he is (world units / radians).
 */
export type CheerKind = 'found' | 'won';

export const CHEER_DUR: Record<CheerKind, number> = { found: 2.5, won: 2.1 };

export interface CheerPose {
  x: number;
  y: number;
  z: number;
  pitch: number;
  yaw: number;
  roll: number;
  /** 1 = side plates tucked in (for the tight moves) */
  arms: number;
  /** Extra LED glow */
  led: number;
}

const TAU = Math.PI * 2;
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const easeInOut = (p: number) => (p < 0.5 ? 4 * p * p * p : 1 - (-2 * p + 2) ** 3 / 2);
const easeOut = (p: number) => 1 - (1 - p) ** 3;
const easeIn = (p: number) => p * p * p;
/** 0 → 1 → 0 over [a, b] */
const bump = (t: number, a: number, b: number) => (t < a || t > b ? 0 : Math.sin((Math.PI * (t - a)) / (b - a)));
/** Eased progress of t through [a, b] */
const span = (t: number, a: number, b: number, e = easeInOut) => e(clamp01((t - a) / (b - a)));

export function cheerPose(kind: CheerKind, t: number): CheerPose {
  const pose: CheerPose = { x: 0, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0, arms: 0, led: 0 };
  if (t < 0 || t > CHEER_DUR[kind]) return pose;

  if (kind === 'found') {
    // Crouch, tremble with excitement
    const crouch = bump(t, 0, 0.42);
    pose.y -= 0.18 * crouch;
    pose.pitch += 0.22 * crouch;
    pose.x += Math.sin(t * 55) * 0.02 * crouch;
    // Spin-launch up (two full turns), backflip at the top, drop back down
    const up = span(t, 0.32, 0.85, easeOut);
    const down = span(t, 1.3, 1.72, easeIn);
    pose.y += 1.05 * up * (1 - down);
    pose.z += 0.35 * up * (1 - down);
    pose.yaw += TAU * 2 * span(t, 0.32, 0.95);
    pose.pitch -= TAU * span(t, 0.85, 1.4);
    // Stick the landing: squash, then a bow
    pose.y -= 0.12 * bump(t, 1.7, 1.95);
    pose.pitch += 0.5 * bump(t, 1.95, 2.45);
    pose.arms = t > 0.3 && t < 1.45 ? 1 : 0;
    pose.led = 1.5 * bump(t, 0.3, 1.8);
  } else {
    // Swoop out left, barrel-roll back across to the right
    const out = span(t, 0, 0.3, easeOut);
    const across = span(t, 0.3, 0.95);
    pose.x += -0.55 * out + 1.1 * across;
    pose.y += 0.35 * bump(t, 0.25, 1.0);
    pose.roll -= TAU * across;
    // Back to the middle with a hopping pirouette
    const home = span(t, 0.95, 1.6);
    pose.x -= 0.55 * home;
    pose.y += 0.45 * bump(t, 1.0, 1.6);
    pose.yaw += TAU * home;
    // Happy shimmy
    pose.roll += Math.sin(t * 24) * 0.12 * bump(t, 1.6, 2.1);
    pose.x += Math.sin(t * 24) * 0.03 * bump(t, 1.6, 2.1);
    pose.arms = (t > 0.3 && t < 0.95) || (t > 1.05 && t < 1.55) ? 1 : 0;
    pose.led = 1.2 * bump(t, 0.2, 1.7);
  }
  return pose;
}
